'use client';

import { type KeyboardEventHandler, forwardRef, useCallback, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { TabsRootContext } from './context/tabs-root.context';
import { tabsPanelRecipe } from './styles/tabs-panel.styles';
import type { TabsPanelPropsInterface } from './types/tabs-panel.types';

/**
 * A `Tabs.Panel` is a child of a `Tabs.Root` component.
 *
 * @since 8.10.0
 */

export const TabsPanel = forwardRef<HTMLDivElement, TabsPanelPropsInterface>(
  (props: TabsPanelPropsInterface, forwardedRef) => {
    const { value, children, className, css: cssProp, onKeyDown, ...rest } = props;

    const { appearance, fluid, selected } = useContext(TabsRootContext);

    const isSelected = useMemo(() => selected === value, [selected, value]);

    const styles = css(tabsPanelRecipe.raw({ appearance, fluid, selected: isSelected }), cssProp);

    /*
     * Prevent arrow key events from bubbling up to the Composite component
     * when focus is within the panel content (e.g., in a modal, input, etc.).
     * This prevents unwanted tab switching when using arrow keys inside panel content.
     */
    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          /*
           * Only stop propagation if the event target is not a tab button.
           * This ensures arrow key navigation still works when focus is on tabs.
           */
          const target = event.target as HTMLElement;
          const isTab =
            target.getAttribute('role') === 'tab' || target.closest('[role="tab"]') !== null;

          if (!isTab) {
            event.stopPropagation();
          }
        }

        onKeyDown?.(event);
      },
      [onKeyDown],
    );

    return (
      <div
        ref={forwardedRef}
        role="tabpanel"
        id={`${value}-panel`}
        aria-labelledby={`${value}-tab`}
        data-c11n-component="Tabs.Panel"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        tabIndex={isSelected ? 0 : -1}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

TabsPanel.displayName = 'Tabs.Panel';
